import React from 'react';
import { Card, Row, Col, Statistic, Progress, Tag, Empty, Tooltip } from 'antd';
import { RiseOutlined, FallOutlined, MinusOutlined } from '@ant-design/icons';

interface EndpointScore {
  endpoint: string;
  score: number;
  errorRate: number;
  avgSpeed: number;
  avgPayloadSize: number;
  totalTransactions: number;
  successCount: number;
  errorCount: number;
  performanceGrade: 'excellent' | 'good' | 'fair' | 'poor';
}

interface EndpointPerformanceTrendProps {
  sourceScore?: EndpointScore;
  destinationScore?: EndpointScore;
  loading?: boolean;
}

const EndpointPerformanceTrend: React.FC<EndpointPerformanceTrendProps> = ({
  sourceScore,
  destinationScore,
  loading
}) => {
  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'excellent': return '#52c41a';
      case 'good': return '#1890ff';
      case 'fair': return '#faad14';
      case 'poor': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getTrendIcon = (score?: number) => {
    if (!score) return <MinusOutlined style={{ color: '#d9d9d9' }} />;
    if (score >= 85) return <RiseOutlined style={{ color: '#52c41a' }} />;
    if (score >= 70) return <RiseOutlined style={{ color: '#1890ff' }} />;
    if (score >= 50) return <MinusOutlined style={{ color: '#faad14' }} />;
    return <FallOutlined style={{ color: '#ff4d4f' }} />;
  };

  const renderEndpointCard = (score?: EndpointScore, title: string) => {
    if (!score) {
      return (
        <Card size="small" loading={loading}>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
            description={`No ${title} data available`}
            style={{ margin: '8px 0' }}
          />
        </Card>
      );
    }

    const color = getGradeColor(score.performanceGrade);

    return (
      <Card size="small" loading={loading}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>{title}</span>
              <Tag color={color} style={{ margin: 0 }}>
                {score.performanceGrade.toUpperCase()}
              </Tag>
            </div>
            <Tooltip title={score.endpoint}>
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                marginBottom: 12
              }}>
                {score.endpoint}
              </div>
            </Tooltip>
          </Col>

          <Col span={24}>
            <Progress 
              percent={score.score} 
              strokeColor={color}
              format={percent => (
                <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  {percent}
                </span>
              )}
            />
          </Col>

          <Col span={8}>
            <Statistic
              title="Error Rate"
              value={score.errorRate}
              suffix="%"
              precision={1}
              valueStyle={{ fontSize: '16px', color: score.errorRate > 5 ? '#ff4d4f' : '#52c41a' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Speed"
              value={score.avgSpeed}
              suffix="ms/KB"
              precision={1}
              valueStyle={{ fontSize: '16px', color: score.avgSpeed < 200 ? '#52c41a' : '#faad14' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Transactions"
              value={score.totalTransactions}
              valueStyle={{ fontSize: '16px' }}
            />
          </Col>

          <Col span={24}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '12px',
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <span style={{ color: '#52c41a' }}>✓ {score.successCount} Success</span>
              <span style={{ color: '#ff4d4f' }}>✗ {score.errorCount} Errors</span>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div>
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          {renderEndpointCard(sourceScore, 'Source Endpoint')}
        </Col>
        <Col xs={24} lg={12}>
          {renderEndpointCard(destinationScore, 'Destination Endpoint')}
        </Col>
      </Row>
    </div>
  );
};

export default EndpointPerformanceTrend;

